import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";

import { Button } from "@av/components/atoms/Button";
import { Image } from "@av/components/atoms/Image";
import { Typography } from "@av/components/atoms/typography/Typography";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

type NotFoundPageProps = {
  ctaButton?: ReactNode;
  handleGoToAppDashboard?: () => void;
  message?: string;
  showGoBackButton?: boolean;
  title?: string;
};

function NotFoundPage(props: NotFoundPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ctaButton, handleGoToAppDashboard, message, showGoBackButton = true, title } = props;

  const resolvedTitle = title ?? t("common.components.errors_notFound_title");

  useDocumentTitle(resolvedTitle);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoToDashboard = () => {
    navigate("/");
  };

  return (
    <>
      <main
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <Image
          testIdPrefix="not-found"
          src="/errors/404_not_found.png"
          alt={t("common.components.errors_notFound_alt")}
          preview={false}
          style={{ maxWidth: "400px", maxHeight: "360px" }}
        />
        <Typography.Title
          testIdPrefix="not-found"
          level={1}
          style={{ marginTop: "0", marginBottom: "8px" }}
        >
          {resolvedTitle}
        </Typography.Title>
        <Typography.Paragraph testIdPrefix="not-found-message" type="secondary">
          {message ?? t("common.components.errors_notFound_message")}
        </Typography.Paragraph>
        <div style={{ marginTop: "24px" }}>
          {showGoBackButton && (
            <Button
              testIdPrefix="not-found-go-back"
              onClick={handleGoBack}
              style={{ marginRight: "10px" }}
              icon={<ArrowLeftOutlined />}
            >
              {t("common.components.errors_notFound_goBack")}
            </Button>
          )}
          {ctaButton ?? (
            <Button
              testIdPrefix="go-to-dashboard"
              type="primary"
              onClick={handleGoToAppDashboard ?? handleGoToDashboard}
            >
              {t("common.components.errors_notFound_goToDashboard")}
            </Button>
          )}
        </div>
      </main>
    </>
  );
}

export { NotFoundPage };
